import "react-markdown-editor-lite/lib/index.css";

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import MdEditor from "react-markdown-editor-lite";

import { renderPage, type RenderDiagnostic } from "./renderPage";
import type { PageFormat } from "./format";
import { useResolvedPageFormat } from "./useResolvedPageFormat";
import { configuredAtlasOrigin, pageFileImageResolver, pageFileUploader, type PageFileTarget } from "./pageFiles";
import { resolvePageEditor, useEditorPreference, type PageEditorKind } from "./editorChoice";
import { getEditorMode, setEditorMode } from "./editorMode";

/** The three modes of `@orbitalwatch/astromark-editor`. The legacy editor has no WYSIWYG surface: it shows split view. */
export type PageEditorMode = "wysiwyg" | "split" | "source";

/** What an upload returns (`AstroMarkUploadedFile` in `@orbitalwatch/astromark-editor`). */
export interface PageEditorUploadedFile {
    url: string;
    name: string;
}

/**
 * The props contract of `AstroMarkEditor` (`@orbitalwatch/astromark-editor` 0.1.0) that Ark uses, plus `format`.
 * Screens depend on this, never on an editor package, so the editor behind it can change without touching them.
 */
export interface PageEditorProps {
    /** The page body. A change from the host replaces the content. */
    value: string;
    /** Called with the whole body after every edit. */
    onChange?: (value: string) => void;
    /**
     * `wysiwyg` (AstroMark editor only; the legacy editor shows split), `split`, `source`. Set: controlled by the screen.
     * Omitted: the editor owns it, starting from the mode the user last picked (localStorage `ark.pageEditor.mode`,
     * `split` at first) and remembering every switch.
     */
    mode?: PageEditorMode;
    /** Called when the user picks another mode with the AstroMark editor's switch (also when the editor owns the mode). */
    onModeChange?: (mode: PageEditorMode) => void;
    /** Show the AstroMark editor's WYSIWYG / Split View / Source switch. Default true. */
    showModeSwitch?: boolean;
    /** Uploads a pasted, dropped or picked file and returns where it lives; the editor inserts `![name](url)`. */
    onUploadFile?: (file: File) => Promise<PageEditorUploadedFile>;
    /** Shorthand for `onUploadFile`: upload to this page's attachments (`PageFiles`). Ignored when `onUploadFile` is set. */
    attachTo?: PageFileTarget;
    /** Turns an image URL into one the browser can show. Default: Ark attachments through the bearer-token cache. */
    resolveImageUrl?: (url: string) => string | Promise<string>;
    /** Findings to list under the editor. Omit to list the preview's own (`renderPage` diagnostics). */
    diagnostics?: RenderDiagnostic[];
    /** Receives the preview's findings whenever they change. */
    onDiagnostics?: (diagnostics: RenderDiagnostic[]) => void;
    /** List the findings under the editor. Default: true when there are any. */
    showDiagnostics?: boolean;
    /** Upload and image failures. Default `console.error`. */
    onError?: (error: Error) => void;
    /**
     * The dialect of the body (pass the page's resolved format). Omitted: `useResolvedPageFormat()` (dev `?format=`, the
     * render-mode switch, else the default), so the editor follows the shell switch. The editor follows the format:
     * markdown-it → legacy editor, astromark → AstroMark editor (`editor` / `?editor=` still win).
     */
    format?: PageFormat;
    /** Which editor (see `PageEditorKind`). Default `auto`; a dev `?editor=legacy|astromark` overrides `auto`. */
    editor?: PageEditorKind;
    /** View only. */
    readOnly?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
    /** Accessible name of the editing surface. Default "Page source". */
    ariaLabel?: string;
    /** AstroMark editor: the heading outline rail. */
    showOutline?: boolean;
    /** Class on the preview (both editors), so the reader's CSS applies. Default {@link READER_BODY_CLASS}. */
    previewClassName?: string;
    className?: string;
}

/** What `PageEditor` hands the editor it picked (everything resolved). */
export interface PageEditorBackendProps extends Omit<PageEditorProps, "diagnostics" | "attachTo" | "editor" | "showDiagnostics"> {
    format: PageFormat;
    mode: PageEditorMode;
    ariaLabel: string;
    diagnostics: RenderDiagnostic[];
    showDiagnostics: boolean;
    atlasOrigin?: string;
}

const AstroMarkPageEditor = lazy(() => import("./AstroMarkPageEditor"));

/** The reader's body class (`reader/reader.css`): the editor previews carry it so they look like the page. */
export const READER_BODY_CLASS = "ark-prose";

const VIEWS: Record<PageEditorMode, { menu: boolean; md: boolean; html: boolean }> = {
    wysiwyg: { menu: true, md: true, html: true },
    split: { menu: true, md: true, html: true },
    source: { menu: true, md: true, html: false },
};

const DIAGNOSTICS_DEBOUNCE_MS = 250;

function sameDiagnostics(a: RenderDiagnostic[], b: RenderDiagnostic[]): boolean {
    return a.length === b.length && a.every((d, i) => d.code === b[i].code && d.message === b[i].message);
}

/** The findings list under the legacy editor (one line per diagnostic, severity first). */
export function PageEditorDiagnostics({ diagnostics }: { diagnostics: RenderDiagnostic[] }) {
    if (!diagnostics.length) return null;
    return (
        <ul className="ark-editor-diagnostics" aria-label="AstroMark diagnostics">
            {diagnostics.map((d, i) => (
                <li key={`${d.code}-${i}`} className={`ark-editor-diagnostic ark-editor-diagnostic--${d.severity}`} data-code={d.code}>
                    <span className="ark-editor-diagnostic__severity">{d.severity}</span> {d.message}
                </li>
            ))}
        </ul>
    );
}

/** `react-markdown-editor-lite` with `renderPage` as the preview (identical to the reader in both formats). */
function LegacyPageEditor(props: PageEditorBackendProps) {
    const { onChange, onUploadFile, format, mode, ariaLabel } = props;

    const renderHTML = useCallback((text: string) => renderPage({ body: text, format }).html, [format]);
    const handleChange = useCallback(({ text }: { text: string; html: string }) => onChange?.(text), [onChange]);
    const onImageUpload = useMemo(
        () => (onUploadFile ? (file: File) => onUploadFile(file).then((uploaded) => uploaded.url) : undefined),
        [onUploadFile]
    );

    // The legacy editor has no aria-label prop: name its text area once it exists.
    const editorRef = useRef<MdEditor>(null);
    useEffect(() => {
        editorRef.current?.getMdElement()?.setAttribute("aria-label", ariaLabel);
    }, [ariaLabel, mode]);

    return (
        <div className={["ark-page-editor", "ark-page-editor--legacy", props.className].filter(Boolean).join(" ")} data-format={format} data-editor="legacy" data-mode={mode}>
            <MdEditor
                ref={editorRef}
                value={props.value}
                onChange={handleChange}
                renderHTML={renderHTML}
                view={VIEWS[mode]}
                readOnly={props.readOnly}
                placeholder={props.placeholder}
                autoFocus={props.autoFocus}
                htmlClass={props.previewClassName}
                onImageUpload={onImageUpload}
                allowPasteImage={!!onImageUpload}
                markdownClass="ark-page-editor__source"
                style={{ height: "100%" }}
                name="page-source"
            />
            {props.showDiagnostics ? <PageEditorDiagnostics diagnostics={props.diagnostics} /> : null}
        </div>
    );
}

/**
 * The page editor screens use (Add Page, Edit Page). Picks the editor (`PageEditorKind`), wires Ark's uploads, attachment
 * images and Atlas origin, and computes the diagnostics with `renderPage` so both editors list the same findings.
 */
export function PageEditor(props: PageEditorProps) {
    const { value, onDiagnostics, attachTo, onModeChange: notifyModeChange } = props;
    const resolvedFormat = useResolvedPageFormat();
    const format = props.format ?? resolvedFormat;
    const [editorPreference] = useEditorPreference();
    const kind = resolvePageEditor(format, props.editor, undefined, undefined, editorPreference);

    // The mode: the screen's when it passes one; otherwise the editor's own, seeded from and written to the stored preference.
    const controlled = props.mode !== undefined;
    const [ownMode, setOwnMode] = useState<PageEditorMode>(() => props.mode ?? getEditorMode());
    const mode = props.mode ?? ownMode;
    const onModeChange = useCallback(
        (next: PageEditorMode) => {
            if (!controlled) {
                setOwnMode(next);
                setEditorMode(next);
            }
            notifyModeChange?.(next);
        },
        [controlled, notifyModeChange]
    );

    const [found, setFound] = useState<RenderDiagnostic[]>(() => renderPage({ body: value, format }).diagnostics);
    useEffect(() => {
        const timer = setTimeout(() => {
            const next = renderPage({ body: value, format }).diagnostics;
            setFound((current) => (sameDiagnostics(current, next) ? current : next));
        }, DIAGNOSTICS_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [value, format]);
    useEffect(() => {
        onDiagnostics?.(found);
    }, [found, onDiagnostics]);

    const attachSpace = attachTo?.spaceId;
    const attachPage = attachTo?.pageId;
    const onUploadFile = useMemo(
        () =>
            props.onUploadFile ??
            (attachSpace !== undefined && attachPage !== undefined ? pageFileUploader({ spaceId: attachSpace, pageId: attachPage }) : undefined),
        [props.onUploadFile, attachSpace, attachPage]
    );

    const images = useMemo(() => pageFileImageResolver(), []);
    useEffect(() => () => images.release(), [images]);

    const shown = props.diagnostics ?? found;
    const backend: PageEditorBackendProps = {
        value,
        onChange: props.onChange,
        mode,
        onModeChange,
        showModeSwitch: props.showModeSwitch,
        onUploadFile,
        resolveImageUrl: props.resolveImageUrl ?? images.resolveImageUrl,
        diagnostics: shown,
        showDiagnostics: props.showDiagnostics ?? shown.length > 0,
        onError: props.onError,
        format,
        readOnly: props.readOnly,
        placeholder: props.placeholder,
        autoFocus: props.autoFocus,
        ariaLabel: props.ariaLabel ?? "Page source",
        showOutline: props.showOutline,
        previewClassName: props.previewClassName ?? READER_BODY_CLASS,
        className: props.className,
        atlasOrigin: configuredAtlasOrigin(),
    };

    if (kind === "legacy") return <LegacyPageEditor {...backend} />;
    return (
        <Suspense fallback={<div className="ark-page-editor ark-page-editor--loading" role="status" aria-label="Loading the editor" />}>
            <AstroMarkPageEditor {...backend} />
        </Suspense>
    );
}
